import React, {useEffect, useRef, useState} from "react";
import {Redirect, useLocation, useParams} from "react-router-dom";
import {Button, Divider, Grid, Toolbar} from "@material-ui/core";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import {makeStyles} from "@material-ui/core/styles";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import {PDFExport} from "@progress/kendo-react-pdf";
import {NotificationManager} from "react-notifications";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {getPdfExportProxyProps, savePdfWithEmbeddedImages} from "../../helpers/pdfExport";
import ActionButton from "../form/input/ActionButton";
import GeneratedDocumentEmailAction from "./GeneratedDocumentEmailAction";
import udzsLogo from "../../assets/documents/udzs-logo.png";
import "../../Print.css";

const useStyles = makeStyles((theme) => ({
    root: {flexGrow: 1, width: "100%", backgroundColor: theme.palette.background.paper},
    seeMore: {marginTop: theme.spacing(8)},
    paper: {padding: theme.spacing(2), display: "flex", overflow: "auto", flexDirection: "column"},
    marginTop: {marginTop: theme.spacing(2)},
    editHint: {margin: "0 0 12px", fontWeight: 700, textAlign: "center", color: "#2c4767"},
    document: {
        boxSizing: "border-box", width: "210mm", height: "297mm", padding: "6mm", overflow: "hidden",
        background: "#fff", color: "#4d4d4d", fontFamily: 'Arial, "DejaVu Sans", sans-serif', fontSize: 10.5,
    },
    pageBorder: {boxSizing: "border-box", display: "flex", flexDirection: "column", minHeight: "283mm", padding: "1.5mm", border: "2px solid #d1d1d1", borderRadius: 3},
    header: {display: "grid", gridTemplateColumns: "58mm 1fr", alignItems: "center", minHeight: "35mm", padding: "3mm", border: "1px solid #cfcfcf", borderRadius: 2},
    logo: {width: "48mm", maxHeight: "22mm", objectFit: "contain"},
    headerText: {textAlign: "center"},
    title: {margin: "0 0 3mm", fontSize: 18, lineHeight: 1.1, color: "#4c4c4c"},
    legal: {margin: "0 auto", maxWidth: "120mm", fontSize: 10.5, lineHeight: 2.5},
    group: {marginTop: "1.8mm", padding: "1.5mm", border: "1px solid #cfcfcf", borderRadius: 2},
    groupTitle: {margin: "-0.5mm -0.5mm 2mm", padding: "1.8mm 2mm", background: "#e8e7e7", borderRadius: 2, fontSize: 11.5, fontWeight: 400},
    grid: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4mm 9mm", padding: "1.5mm 2mm 2.5mm"},
    full: {gridColumn: "1 / -1"},
    label: {display: "block", color: "#4d4d4d"},
    input: {boxSizing: "border-box", width: "100%", height: "6.5mm", marginTop: "2mm", padding: "1mm 2mm", border: "1px solid #c8c8c8", boxShadow: "1px 1px 0 #b8b8b8", background: "#fff", color: "#333", fontFamily: 'Arial, "DejaVu Sans", sans-serif', fontSize: 11},
    shortInput: {width: "46%"},
    declarationTitle: {lineHeight: 1.35},
    declaration: {padding: "2mm 3mm 3mm", textAlign: "center", fontSize: 11.5},
    closing: {display: "grid", gridTemplateColumns: "8mm 1fr 8mm 46mm 1fr", gap: "2mm", alignItems: "end", margin: "11mm 3mm 0"},
    closingInput: {height: "6mm", margin: 0},
    signature: {paddingTop: "1mm", borderTop: "1px solid #555", textAlign: "center", fontWeight: 700},
    footer: {display: "flex", justifyContent: "space-between", margin: "auto 2mm 0", paddingTop: "3mm", fontSize: 8},
    compact: {
        "& $header": {minHeight: "31mm"},
        "& $legal": {lineHeight: 2.2},
        "& $group": {marginTop: "1.4mm"},
        "& $grid": {gap: "2.5mm 9mm", paddingTop: "1mm", paddingBottom: "1.5mm"},
        "& $input": {height: "5.5mm", marginTop: "1.2mm"},
        "& $closing": {marginTop: "5mm"},
    },
}));

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("sk-SK", {day: "2-digit", month: "2-digit", year: "numeric"});
};

const splitName = (value = "") => {
    const parts = value.trim().split(/\s+/).filter(Boolean);
    return {firstName: parts.slice(0, -1).join(" ") || parts[0] || "", lastName: parts.length > 1 ? parts[parts.length - 1] : ""};
};

const splitAddress = (value = "") => {
    const match = value.trim().match(/^(.*?)(?:\s+)(\d+[A-Za-z/-]*)$/);
    return match ? {street: match[1], number: match[2]} : {street: value || "", number: ""};
};

const initialFields = {
    personFirstName: "", personLastName: "", personId: "", personStreet: "", personNumber: "", personZip: "", personCity: "", personState: "",
    representativeFirstName: "", representativeLastName: "", representativeId: "", representativeStreet: "", representativeNumber: "", representativeZip: "", representativeCity: "", representativeState: "",
    location: "", signedDate: formatDate(new Date()),
};

const RefusalContext = React.createContext(null);

const FormInput = ({name, short = false}) => {
    const {classes, fields, handleChange} = React.useContext(RefusalContext);
    return <input name={name} value={fields[name]} onChange={handleChange} className={`${classes.input} ${short ? classes.shortInput : ""}`}/>;
};

const PersonFields = ({prefix, addressTitle}) => {
    const {classes} = React.useContext(RefusalContext);
    return (
        <>
            <div className={classes.grid}>
                <label className={classes.label}>Meno<FormInput name={`${prefix}FirstName`}/></label>
                <label className={classes.label}>Priezvisko<FormInput name={`${prefix}LastName`}/></label>
                <label className={`${classes.label} ${classes.full}`}>Rodné číslo / Dátum narodenia<FormInput name={`${prefix}Id`} short/></label>
            </div>
            <div className={classes.groupTitle}>{addressTitle}</div>
            <div className={classes.grid}>
                <label className={classes.label}>Ulica<FormInput name={`${prefix}Street`}/></label>
                <label className={classes.label}>Číslo<FormInput name={`${prefix}Number`}/></label>
                <label className={classes.label}>PSČ<FormInput name={`${prefix}Zip`}/></label>
                <label className={classes.label}>Obec<FormInput name={`${prefix}City`}/></label>
                <label className={`${classes.label} ${classes.full}`}>Štát<br/><FormInput name={`${prefix}State`} short/></label>
            </div>
        </>
    );
};

const AutopsyRefusalGenerator = (props) => {
    const classes = useStyles();
    const pdfRef = useRef(null);
    const funeralId = useParams();
    const location = useLocation();
    const hasRepresentative = location.pathname.includes("zakonny-zastupca");
    const userLocal = JSON.parse(localStorage.getItem("user")).id;
    const [funeral, setFuneral] = useState({_id: "", deceased: "", ca_email: ""});
    const [user, setUser] = useState(null);
    const [fields, setFields] = useState(initialFields);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const handleLoadError = (error) => {
            if (error.response?.status === 401) {
                logOut(props);
                NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
            }
        };

        UserService.getUser(userLocal).then(({data}) => {
            setUser(data);
            setFields((current) => ({...current, location: data.defined_texts?.default_signature_place || ""}));
        }, handleLoadError);

        UserService.getFuneral(funeralId.funeral_id).then(({data}) => {
            const deceasedName = splitName(data.deceased);
            const deceasedAddress = splitAddress(data.d_address);
            const representativeName = splitName(data.contracting_authority);
            const representativeAddress = splitAddress(data.ca_address);
            setFuneral(data);
            setFields((current) => ({...current,
                personFirstName: deceasedName.firstName,
                personLastName: deceasedName.lastName,
                personId: data.d_id || formatDate(data.d_birth),
                personStreet: deceasedAddress.street,
                personNumber: deceasedAddress.number,
                personZip: data.d_zip || "",
                personCity: data.d_city || "",
                personState: data.d_state || "",
                representativeFirstName: representativeName.firstName,
                representativeLastName: representativeName.lastName,
                representativeId: data.ca_id || formatDate(data.ca_birth),
                representativeStreet: representativeAddress.street,
                representativeNumber: representativeAddress.number,
                representativeZip: data.ca_zip || "",
                representativeCity: data.ca_city || "",
                representativeState: data.ca_state || "",
            }));
        }, handleLoadError);
    }, [funeralId.funeral_id, userLocal, props]);

    const handleChange = ({target: {name, value}}) => setFields((current) => ({...current, [name]: value}));
    const handleExport = () => savePdfWithEmbeddedImages(pdfRef, (error) => {
        if (error) NotificationManager.error("Nepodarilo sa exportovať PDF!", "Chyba", 5000);
    });
    const fileSuffix = (funeral.deceased || "zosnuly").replace(/\s/g, "");
    const documentCode = hasRepresentative ? "F-820/0" : "F-819/0";

    return (
        <Container maxWidth="lg" className={classes.seeMore}>
            <div className={classes.root}><Paper className={classes.paper}>
                <Grid container>
                    <Grid xs={12} sm={4} md={3} item container><Toolbar><Button variant="contained" color="secondary" onClick={() => setSuccess(true)}>Späť na detaily</Button></Toolbar></Grid>
                    <Grid xs={12} sm={8} md={9} item container justifyContent="flex-end"><Toolbar className="document-actions-toolbar">
                        <ActionButton color="primary"><Tooltip title="Pdf export"><PictureAsPdfIcon fontSize="small" onClick={handleExport}/></Tooltip></ActionButton>
                        <GeneratedDocumentEmailAction pdfRef={pdfRef} funeral={funeral} user={user}/>
                    </Toolbar></Grid>
                </Grid>
                <Divider/>
                <Grid className={classes.marginTop} container direction="column" alignItems="center">
                    <div className={classes.editHint}>Po KLIKNUTÍ viete dopisovať alebo meniť údaje.</div>
                    <div className="document-preview-frame">
                        <PDFExport ref={pdfRef} {...getPdfExportProxyProps()} paperSize="A4" scale={0.75} fileName={`${hasRepresentative ? "odmietnutie-pitvy-zakonny-zastupca" : "odmietnutie-pitvy"}-${fileSuffix}`}>
                            <RefusalContext.Provider value={{classes, fields, handleChange}}>
                                <div className={`a4-paper ${classes.document} ${hasRepresentative ? classes.compact : ""}`}>
                                    <div className={classes.pageBorder}>
                                        <header className={classes.header}>
                                            <img src={udzsLogo} className={classes.logo} alt="Úrad pre dohľad nad zdravotnou starostlivosťou"/>
                                            <div className={classes.headerText}>
                                                <h1 className={classes.title}>Odmietnutie pitvy</h1>
                                                <div className={classes.legal}>podľa § 48 ods. 4 zákona č. 581/2004 Z.z. o zdravotných poisťovniach, dohľade nad zdravotnou starostlivosťou a o zmene a doplnení niektorých zákonov v znení neskorších predpisov</div>
                                            </div>
                                        </header>

                                        <section className={classes.group}>
                                            <div className={classes.groupTitle}>Osoba, ktorá odmietla pitvu</div>
                                            <PersonFields prefix="person" addressTitle="Adresa pobytu osoby, ktorá odmietla pitvu"/>
                                        </section>

                                        {hasRepresentative && <section className={classes.group}>
                                            <div className={classes.groupTitle}>Zákonný zástupca</div>
                                            <PersonFields prefix="representative" addressTitle="Adresa pobytu zákonného zástupcu"/>
                                        </section>}

                                        <section className={classes.group}>
                                            <div className={`${classes.groupTitle} ${classes.declarationTitle}`}>Prehlásenie o odmietnutí pitvy podľa § 48 ods. 4 zákona č. 581/2004 Z.z. o zdravotných poisťovniach, dohľade nad zdravotnou starostlivosťou a o zmene a doplnení niektorých zákonov v znení neskorších predpisov</div>
                                            <div className={classes.declaration}>{hasRepresentative ? "Prehlasujem, že v prípade úmrtia osoby, ktorej som zákonným zástupcom, odmietam vykonanie pitvy." : "Prehlasujem, že v prípade môjho úmrtia, odmietam vykonanie pitvy."}</div>
                                        </section>

                                        <div className={classes.closing}>
                                            <strong>V</strong><FormInput name="location"/>
                                            <strong>Dňa</strong><FormInput name="signedDate"/>
                                            <div className={classes.signature}>podpis podávajúceho</div>
                                        </div>
                                        <div className={classes.footer}><span>KP č. HP-01</span><span>{documentCode}</span></div>
                                    </div>
                                </div>
                            </RefusalContext.Provider>
                        </PDFExport>
                    </div>
                </Grid>
            </Paper></div>
            {success ? <Redirect to={{pathname: `/pohreb/${funeralId.funeral_id}/info`, state: {from: props.location, number: 5}}}/> : <></>}
        </Container>
    );
};

export default AutopsyRefusalGenerator;
